/**
 * Widget Order Service
 *
 * Handles order management API calls for widget customers.
 * Methods: list orders, get order detail, request pickup, cancel order
 */

import { WidgetApiClient } from './WidgetApiClient';
import type {
  Order,
  OrderListResponse,
  OrderFilters,
  RequestPickupPayload,
  CancelOrderPayload,
} from '../../types/widget/order';

export class WidgetOrderService {
  private apiClient: WidgetApiClient;
  private providerSlug: string;

  constructor(providerSlug: string, apiClient: WidgetApiClient) {
    this.providerSlug = providerSlug;
    this.apiClient = apiClient;
  }

  /**
   * Get list of orders with optional filters
   */
  async listOrders(filters: OrderFilters = {}): Promise<OrderListResponse> {
    const params = new URLSearchParams();

    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.pageSize) {
      params.append('pageSize', filters.pageSize.toString());
    }

    const queryString = params.toString();
    const endpoint = `/api/v1/widget/${this.providerSlug}/orders${
      queryString ? `?${queryString}` : ''
    }`;

    return this.apiClient.get<OrderListResponse>(endpoint);
  }

  /**
   * Get order detail by ID
   */
  async getOrder(orderId: string): Promise<Order> {
    return this.apiClient.get<Order>(
      `/api/v1/widget/${this.providerSlug}/orders/${orderId}`
    );
  }

  /**
   * Request pickup for an order
   */
  async requestPickup(
    orderId: string,
    payload: RequestPickupPayload
  ): Promise<Order> {
    return this.apiClient.post<Order>(
      `/api/v1/widget/${this.providerSlug}/orders/${orderId}/request-pickup`,
      payload
    );
  }

  /**
   * Cancel an order
   */
  async cancelOrder(orderId: string, payload: CancelOrderPayload): Promise<Order> {
    return this.apiClient.post<Order>(
      `/api/v1/widget/${this.providerSlug}/orders/${orderId}/cancel`,
      payload
    );
  }
}
